import type { ReactNode } from "react";
import type { FernieWindowHandle, FernieWindowOpenResult } from "~/helpers/aiChat/openFernieWindow";

import { CacheProvider } from "@emotion/react";
import { Box, CssBaseline, ThemeProvider, useTheme } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import shouldIgnoreFernieUnload, { FERNIE_UNLOAD_GRACE_MS } from "~/helpers/aiChat/shouldIgnoreFernieUnload";
import { useSnacks } from "~/providers/SnackProvider";

interface FerniePopoutWindowProps {
	children: ReactNode;
	onBlocked: () => void;
	onClose: () => void;
	opening: FernieWindowOpenResult;
}

const FerniePopoutWindow = ({ children, onBlocked, onClose, opening }: FerniePopoutWindowProps) => {
	const theme = useTheme();
	const { t } = useTranslation("common");
	const [openSnack] = useSnacks();
	const [handle, setHandle] = useState<FernieWindowHandle | null>(null);
	const callbacks = useRef({ onBlocked, onClose, openSnack, t });
	callbacks.current = { onBlocked, onClose, openSnack, t };
	const popoutTheme = useMemo(
		() =>
			createTheme(theme, {
				components: {
					MuiDialog: { defaultProps: { container: handle?.container } },
					MuiModal: { defaultProps: { container: handle?.container } },
					MuiPopover: { defaultProps: { container: handle?.container } }
				}
			}),
		[handle, theme]
	);

	useEffect(() => {
		let cancelled = false;
		let removeCloseListener: (() => void) | undefined;
		let closeCheck: ReturnType<typeof setTimeout> | undefined;

		opening.ready.then((opened) => {
			if (cancelled) return;
			if (opening.window?.closed) {
				callbacks.current.onClose();
				return;
			}
			if (!opened) {
				callbacks.current.openSnack(callbacks.current.t("fernieChat.panel.popoutBlocked"), "warning");
				callbacks.current.onBlocked();
				return;
			}

			setHandle(opened);
			const openedAt = Date.now();
			const unloadEventType = opened.kind === "document-pip" ? "pagehide" : "beforeunload";
			const handleUnload = () => {
				if (opened.kind === "popup" && shouldIgnoreFernieUnload(openedAt, Date.now())) {
					clearTimeout(closeCheck);
					closeCheck = setTimeout(() => {
						if (opened.window.closed) callbacks.current.onClose();
					}, FERNIE_UNLOAD_GRACE_MS);
					return;
				}
				callbacks.current.onClose();
			};
			opened.window.addEventListener(unloadEventType, handleUnload);
			removeCloseListener = () => opened.window.removeEventListener(unloadEventType, handleUnload);
		});

		return () => {
			cancelled = true;
			clearTimeout(closeCheck);
			removeCloseListener?.();
			opening.close();
		};
	}, [opening]);

	if (!handle) return null;

	return createPortal(
		<CacheProvider value={handle.cache}>
			<ThemeProvider theme={popoutTheme}>
				<CssBaseline />
				<Box sx={{ backgroundColor: "background.default", height: "100%", overflow: "hidden" }}>{children}</Box>
			</ThemeProvider>
		</CacheProvider>,
		handle.container
	);
};

export default FerniePopoutWindow;
